import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="splash--background"
// Renders a full-bleed animated WebGL gradient behind the landing page hero.
export default class extends Controller {
  static targets = ["canvas"];

  connect() {
    if (!this.hasCanvasTarget) return;

    const canvas = this.canvasTarget;
    // GL context hints tuned for Safari:
    //   powerPreference: "low-power"   → integrated GPU on multi-GPU laptops, avoids
    //                                    a discrete-GPU switch latency on first frame
    //   antialias: false               → fullscreen quad has no internal geometry edges
    //   failIfMajorPerformanceCaveat   → bail rather than fall back to slow software
    //                                    rasterizer on machines without GPU acceleration
    const glAttrs = {
      alpha: true,
      antialias: false,
      premultipliedAlpha: false,
      powerPreference: "low-power",
      failIfMajorPerformanceCaveat: true,
    };
    const gl = canvas.getContext("webgl", glAttrs) || canvas.getContext("experimental-webgl", glAttrs);

    if (!gl) {
      this.revealPage();
      return;
    }

    this.gl = gl;
    this.canvas = canvas;

    const vsSource = `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    // Adapted from the reference preview shader but retuned to brand palette
    // (deep blue → electric blue → green highlight) and softened for a subtle
    // behind-hero feel.
    const fsSource = `
      precision highp float;
      uniform vec2 u_resolution;
      uniform float u_time;
      uniform vec2 u_mouse;
      uniform float u_scroll;

      void main() {
        vec2 uv = gl_FragCoord.xy / u_resolution.xy;
        vec2 p = uv;

        vec2 mouse = u_mouse / u_resolution.xy;
        mouse.y = 1.0 - mouse.y;

        float dist = distance(uv, mouse);
        float hover = smoothstep(0.45, 0.0, dist);
        p.y += sin(p.x * 7.0 - u_time * 1.6) * 0.04 * hover;

        // Scroll drives an analogous palette drift that returns to brand at 0 and 1.
        float scrollWave = sin(clamp(u_scroll, 0.0, 1.0) * 3.14159265);
        vec3 blueBrand  = vec3(0.05, 0.35, 0.95);
        vec3 greenBrand = vec3(0.15, 0.85, 0.60);
        vec3 blueDrift  = vec3(0.28, 0.24, 0.98);
        vec3 greenDrift = vec3(0.05, 0.78, 0.82);
        vec3 blueMix  = mix(blueBrand,  blueDrift,  scrollWave * 0.55);
        vec3 greenMix = mix(greenBrand, greenDrift, scrollWave * 0.55);

        vec3 glowBrand = vec3(0.12, 0.28, 0.58);
        vec3 glowDrift = vec3(0.22, 0.22, 0.62);
        vec3 glowColor = mix(glowBrand, glowDrift, scrollWave * 0.45);

        vec3 color = vec3(0.03, 0.04, 0.07);

        float bgGlow = 1.0 - distance(p, vec2(0.5, 0.4));
        color += glowColor * bgGlow * 0.35;

        for (float i = 1.0; i <= 6.0; i++) {
          float t = u_time * 0.18 + i * 1.6;

          float waveY = 0.5
                      + sin(p.x * 2.2 + t) * 0.22
                      + cos(p.x * 1.4 - t * 0.8) * 0.16;

          waveY += sin(p.x * 1.5 + t * 0.6) * 0.3 * p.x;

          float waveBody = smoothstep(waveY + 0.06, waveY - 0.28, p.y);
          float waveCrest = smoothstep(waveY + 0.025, waveY - 0.025, p.y)
                          * smoothstep(waveY - 0.09, waveY + 0.025, p.y);

          vec3 waveColor = mix(blueMix, greenMix, i / 6.0);

          float fade = smoothstep(1.2, 0.0, p.x)
                     * smoothstep(0.0, 0.3, p.y) * smoothstep(1.0, 0.7, p.y);

          color += waveColor * waveBody * 0.14 * fade;
          color += waveColor * waveCrest * 0.38 * fade;
        }

        gl_FragColor = vec4(color, 1.0);
      }
    `;

    const compile = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vs = compile(gl.VERTEX_SHADER, vsSource);
    const fs = compile(gl.FRAGMENT_SHADER, fsSource);
    if (!vs || !fs) return;

    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);
    this.program = program;

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, "position");
    this.resolutionLocation = gl.getUniformLocation(program, "u_resolution");
    this.timeLocation = gl.getUniformLocation(program, "u_time");
    this.mouseLocation = gl.getUniformLocation(program, "u_mouse");
    this.scrollLocation = gl.getUniformLocation(program, "u_scroll");

    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    this.mouseX = window.innerWidth / 2;
    this.mouseY = window.innerHeight / 2;
    this.targetMouseX = this.mouseX;
    this.targetMouseY = this.mouseY;
    this.startTime = performance.now();
    this.scrollProgress = 0;
    this.targetScrollProgress = 0;

    this.onResize = this.resize.bind(this);
    this.onMouseMove = this.handleMouseMove.bind(this);
    this.onScroll = this.handleScroll.bind(this);
    window.addEventListener("resize", this.onResize);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("scroll", this.onScroll, { passive: true });

    this.resize();
    this.handleScroll();
    this.running = true;
    this.canvasVisible = true;

    // Pause the render loop when the canvas scrolls out of the viewport
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.canvasVisible = entry.isIntersecting;
          if (this.canvasVisible && this.running && this.renderStarted && !this.animationFrame) {
            this.render();
          }
        });
      },
      { threshold: 0 },
    );
    this.visibilityObserver.observe(this.canvas);

    this.scheduleRenderStart();
  }

  // Defer starting the rAF render loop until after initial paint settles.
  // During the first ~1.5s, GPU is busy with font upload, image decode, and
  // initial composite; running our full-bleed shader concurrently was costing
  // 100ms+ per rAF callback in real Safari (verified via ablation traces).
  scheduleRenderStart() {
    const trigger = () => {
      if (this.renderStarted || !this.running) return;
      this.renderStarted = true;
      this.removeRenderStartListeners();
      this.render();
    };
    this.renderStartTrigger = trigger;

    this.renderStartDelayHandle = window.setTimeout(() => {
      const idle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 0));
      this.renderStartIdleHandle = idle(trigger, { timeout: 4000 });
    }, 1500);

    // Or kick off immediately on first user interaction.
    this.renderStartEvents = ["pointerdown", "keydown"];
    this.renderStartEvents.forEach((ev) => window.addEventListener(ev, trigger, { once: true, passive: true }));
  }

  removeRenderStartListeners() {
    if (this.renderStartDelayHandle) {
      window.clearTimeout(this.renderStartDelayHandle);
      this.renderStartDelayHandle = null;
    }
    if (window.cancelIdleCallback && this.renderStartIdleHandle) {
      window.cancelIdleCallback(this.renderStartIdleHandle);
    }
    this.renderStartIdleHandle = null;
    if (this.renderStartEvents && this.renderStartTrigger) {
      this.renderStartEvents.forEach((ev) => window.removeEventListener(ev, this.renderStartTrigger));
    }
  }

  disconnect() {
    this.running = false;
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.removeRenderStartListeners();
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    if (this.onResize) window.removeEventListener("resize", this.onResize);
    if (this.onMouseMove) window.removeEventListener("mousemove", this.onMouseMove);
    if (this.onScroll) window.removeEventListener("scroll", this.onScroll);
  }

  dpr() {
    return Math.min(window.devicePixelRatio || 1, 2);
  }

  resize() {
    const dpr = this.dpr();
    this.canvas.width = Math.floor(this.canvas.clientWidth * dpr);
    this.canvas.height = Math.floor(this.canvas.clientHeight * dpr);
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  handleMouseMove(event) {
    const rect = this.canvas.getBoundingClientRect();
    this.targetMouseX = (event.clientX - rect.left) * this.dpr();
    this.targetMouseY = (event.clientY - rect.top) * this.dpr();
  }

  handleScroll() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    this.targetScrollProgress = scrollable > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollable)) : 0;
  }

  render() {
    if (!this.running || !this.canvasVisible) {
      this.animationFrame = null;
      return;
    }

    // Cap effective framerate at ~30fps. The animated gradient is decorative
    // and slow-moving; rendering every other frame halves GPU + main-thread
    // cost with no visible effect.
    const now = performance.now();
    const minFrameInterval = 33;
    if (this.lastRenderAt != null && now - this.lastRenderAt < minFrameInterval) {
      this.animationFrame = requestAnimationFrame(() => this.render());
      return;
    }
    this.lastRenderAt = now;

    this.mouseX += (this.targetMouseX - this.mouseX) * 0.08;
    this.mouseY += (this.targetMouseY - this.mouseY) * 0.08;
    this.scrollProgress += (this.targetScrollProgress - this.scrollProgress) * 0.06;

    this.gl.uniform2f(this.resolutionLocation, this.canvas.width, this.canvas.height);
    this.gl.uniform1f(this.timeLocation, (now - this.startTime) * 0.001);
    this.gl.uniform2f(this.mouseLocation, this.mouseX, this.mouseY);
    this.gl.uniform1f(this.scrollLocation, this.scrollProgress);

    this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
    if (!this.revealed) this.revealPage();
    this.animationFrame = requestAnimationFrame(() => this.render());
  }

  revealPage() {
    this.revealed = true;
    this.element.closest("main.splash")?.classList.add("splash-ready");
  }
}
